import type { AssistanceServiceConfig } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { AssistanceServiceBase } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import type {
	InsightsStatusResponse,
	InsightsFeedResponse,
} from '@atlassian/rovo-insights-service-api/types';

export type RovoInsightsServiceConfig = AssistanceServiceConfig;

const CHANNEL_ID_HEADER = 'X-Channel-Id';
const PRODUCT_HEADER = 'x-product';
const EXPERIENCE_ID_HEADER = 'x-experience-id';

/**
 * Service for Rovo Insights API.
 *
 * Extends AssistanceServiceBase to reuse URL construction, header management,
 * and request handling while keeping Rovo Insights concerns separate from the
 * general-purpose conversation assistant service.
 */
export class RovoInsightsService extends AssistanceServiceBase {
	constructor(config: RovoInsightsServiceConfig) {
		super(config);
	}

	/**
	 * Check whether Rovo Insights is available for the current user.
	 */
	public async getInsightsStatus(): Promise<InsightsStatusResponse> {
		return this.fetchJson<InsightsStatusResponse>({
			path: '/rovo/v1/insights/status',
			options: {
				method: 'POST',
				headers: this.createHeaders({
					'Content-Type': 'application/json;charset=UTF-8',
					[CHANNEL_ID_HEADER]: 'rovo-chat',
					[PRODUCT_HEADER]: 'rovo',
					[EXPERIENCE_ID_HEADER]: 'rovo-insights',
				}),
				body: JSON.stringify({}),
			},
			holdName: 'rovo-v1-insights-status',
		});
	}

	/**
	 * Fetch the Rovo Insights feed for the current user.
	 * @param options.debugInfo — When true, include debug_info in the response.
	 */
	public async getInsightsFeed(options?: { debugInfo?: boolean }): Promise<InsightsFeedResponse> {
		const overrides: Record<string, string | boolean> = {};

		if (options?.debugInfo) {
			overrides.debug_info = true;
		}

		if (typeof localStorage !== 'undefined') {
			// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
			const promptVariation = localStorage.getItem('DEBUG_rovo_insights_prompt_variation');
			if (promptVariation) {
				overrides.prompt_variation = promptVariation;
			}

			// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
			const generate = localStorage.getItem('DEBUG_rovo_insights_generate');
			if (generate) {
				overrides.generate = generate === 'true';
			}
		}

		return this.fetchJson<InsightsFeedResponse>({
			path: '/rovo/v1/insights/fetch',
			options: {
				method: 'POST',
				headers: this.createHeaders({
					'Content-Type': 'application/json;charset=UTF-8',
					[CHANNEL_ID_HEADER]: 'rovo-chat',
					[PRODUCT_HEADER]: 'rovo',
					[EXPERIENCE_ID_HEADER]: 'rovo-insights',
				}),
				body: JSON.stringify(overrides),
			},
			holdName: 'rovo-v1-insights-fetch',
		});
	}
}
